import React from 'react';
import PropTypes from 'prop-types';
import Case from 'case';
import {
  Box, Text,
} from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, ActionButton } from '@Components/Control';
import { ProductFormSingleTextInput } from '@Components/Partial/Product/NewSearch';
import { localAsset } from '@Helpers/asset';
import { pluralize } from '@Helpers/util';
import {
  renderTableHeader,
  tablePadding,
  NoDataPlaceholder,
  TableWrapper,
} from '../SharedComponents';
import { StyledSVG } from '../StyledSharedComponents';


const TableControls = ({
  selectedSortOption,
  handleSort,
  handleAdd,
  isAdding,
  iconPath,
  actionTerm,
  controlsDisabled = false,
}) => {
  const { theme: { page, button } } = useAppTheme();

  return (
    <Box direction="row" gap="0.5rem">
      <AppButton
        overrideHover
        onClick={() => handleAdd()}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={button.primaryBg}
        fontWeight={600}
        label={isAdding ? 'Cancel' : `+ Add ${Case.capital(actionTerm)}`}
        disabled={controlsDisabled}
      />
      <AppButton
        plain
        overrideHover
        sortButtonStyle
        key="sort"
        textSize="1rem"
        color={page.inputText}
        label={selectedSortOption.name}
        highlight={page.focusHighlight}
        onClick={() => handleSort(selectedSortOption.direction === 'desc' ? 'asc' : 'desc')}
        icon={(
          <StyledSVG
            src={localAsset(iconPath)}
            width="1.1rem"
            height="1.1rem"
            title={selectedSortOption.name}
            $fillColor={page.inputIcon}
          />
        )}
      />
    </Box>
  );
};

TableControls.propTypes = {
  iconPath: PropTypes.string.isRequired,
  selectedSortOption: PropTypes.shape({
    name: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  handleSort: PropTypes.func.isRequired,
  handleAdd: PropTypes.func.isRequired,
  isAdding: PropTypes.bool.isRequired,
  actionTerm: PropTypes.string.isRequired,
  controlsDisabled: PropTypes.bool,
};

const DomainListTable = ({
  small,
  loading,
  actionTerm,
  domainData = null,
  pageTitle = null,
  headerMap,
  updateDomain = null,
  noTablePad = false,
  unitsApiMode = false,
  updateUnitsRequest = null,
  paymentPending = false,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValues, setEditValues] = React.useState({ newValue: '', originalValue: '' });
  const [isAdding, setIsAdding] = React.useState(false);
  const [addValue, setAddValue] = React.useState('');
  const [sortedData, setSortedData] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState({ name: 'Sort A-Z', direction: 'desc' });
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    if (domainData) {
      const initialData = [...domainData];
      const sorted = initialData.sort((a, b) => (a.localeCompare(b)));
      setSortedData(sorted);
    }
  }, [domainData]);

  const handleTableAction = (value, preValue, actionType) => {
    if (paymentPending) return;
    if (unitsApiMode && updateUnitsRequest) {
      let newUnits = [...(sortedData || [])];

      if (actionType === 'push' && value) {
        newUnits.push(value);
      } else if (actionType === 'edit' && value && preValue) {
        const idx = newUnits.indexOf(preValue);
        if (idx !== -1) newUnits[idx] = value;
      } else if (actionType === 'delete' && value) {
        newUnits = newUnits.filter((team) => team === value);
      }

      setSortedData(newUnits);
      updateUnitsRequest(newUnits, actionType);
    } else {
      updateDomain(value, preValue, actionType);
    }
  };

  const handleEdit = (val) => {
    if (paymentPending) return;
    setEditValues({ newValue: val, originalValue: val });
    setIsEditing(true);
  };

  const handleSort = (sortDirection) => {
    const prevData = [...(sortedData || [])];

    if (sortDirection === 'desc') {
      prevData.sort((a, b) => (a.localeCompare(b)));
      setSortOrder({ name: 'Sort A-Z', direction: 'desc' });
    } else {
      prevData.sort((a, b) => (b.localeCompare(a)));
      setSortOrder({ name: 'Sort Z-A', direction: 'asc' });
    }

    setSortedData(prevData);
  };

  const editSubmitDisabled = isEditing && editValues.newValue === editValues.originalValue;
  const addSubmitDisabled = isAdding && (!addValue || addValue === '');

  const renderCellChildren = (childValue) => {
    if (isEditing && editValues.originalValue === childValue) {
      return (
        <Box key={`row-${childValue}`} direction="row" width={small ? '100%' : '50%'} gap="0.5rem">
          <Box flex>
            <ProductFormSingleTextInput
              key={childValue}
              focusHighlight={page.focusHighlight}
              value={editValues.newValue}
              handleFormValues={(val) => (
                setEditValues({ newValue: val, originalValue: childValue })
              )}
              placeholder={`Enter a new ${actionTerm}`}
              small={small}
              onClear={() => {
                setEditValues({ newValue: '', originalValue: '' });
                setIsEditing(false);
              }}
              clearActive={isEditing}
            />
          </Box>
          <Box pad={{ vertical: '0.15rem' }}>
            <AppButton
              overrideHover
              onClick={() => {
                handleTableAction(editValues.newValue, editValues.originalValue, 'edit');
                setEditValues({ newValue: '', originalValue: '' });
                setIsEditing(false);
              }}
              disabled={editSubmitDisabled}
              level="tableControls"
              color={button.primaryText}
              bgColor={button.primaryBg}
              fontWeight={600}
              label="Save"
            />
          </Box>
        </Box>
      );
    }

    return (
      <Text size="1rem" color={page.primaryText}>{childValue}</Text>
    );
  };

  const renderTableContents = () => {
    if (loading || !sortedData || sortedData.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          buttonHighlight={button.primaryBg}
          loading={loading || (!loading && !sortedData)}
          label={`No ${pluralize(actionTerm)} in this category`}
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    const editButtonTitle = `Edit ${actionTerm}`;
    const deleteButtonTitle = `Delete ${actionTerm}`;

    return (
      <Box fill="horizontal" direction="column">
        {sortedData?.map((dt) => (
          <Box
            pad={{ vertical: '0.75rem', horizontal: '0.5rem' }}
            key={dt}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            direction="row"
            fill="horizontal"
          >
            <Box width={small ? '75%' : '85%'} justify="center">
              {renderCellChildren(dt)}
            </Box>
            <Box width={small ? '25%' : '15%'} pad={{ left: '0.5rem' }}>
              <Box direction="row" round="var(--app-radius)" gap="0.5rem">
                <ActionButton
                  key="edit-item"
                  small={small}
                  buttonName={editButtonTitle}
                  iconPath="images/edit-pencil-icon.svg"
                  textColor={page.tableText}
                  hoverColor={button.hoverBg}
                  hoverTextColor={button.hoverText}
                  actionHandler={() => handleEdit(dt)}
                  disabled={paymentPending}
                />
                <ActionButton
                  key="delete-item"
                  small={small}
                  buttonName={deleteButtonTitle}
                  iconPath="images/delete-content-icon.svg"
                  textColor={page.tableText}
                  hoverColor={button.hoverBg}
                  hoverTextColor={button.hoverText}
                  actionHandler={() => handleTableAction(dt, null, 'delete')}
                  disabled={paymentPending}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box direction="column" fill="horizontal">
      <TableWrapper
        small={small}
        header={pageTitle}
        boxPad={noTablePad ? '0' : tablePadding(small)}
        tableControls={(
          <TableControls
            small={small}
            displayInputValue={null}
            actionTerm={actionTerm}
            selectedSortOption={sortOrder}
            handleSort={(sortDirection) => handleSort(sortDirection)}
            handleAdd={() => setIsAdding(!isAdding)}
            isAdding={isAdding}
            iconPath={`images/sort-input-icon-${sortOrder.direction}.svg`}
            controlsDisabled={paymentPending}
          />
        )}
      >
        <Box>
          {!small && sortedData?.length > 0 && (
            <Box
              fill="horizontal"
              wrap={false}
              direction="row"
              border={{
                color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
            >
              {headerMap.map(({ uid, display, size }) => (
                <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                  {renderTableHeader(display, small)}
                </Box>
              ))}
            </Box>
          )}
          {isAdding && (
            <Box
              pad={{ vertical: '0.75rem', horizontal: '0.5rem' }}
              border={{
                color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
              width="100%"
            >
              <Box width={small ? '100%' : '50%'} direction="row" gap="0.5rem">
                <Box flex>
                  <ProductFormSingleTextInput
                    focusHighlight={page.focusHighlight}
                    value={addValue}
                    handleFormValues={(val) => setAddValue(val)}
                    placeholder={`Enter a new ${actionTerm}`}
                    small={small}
                    onClear={() => {
                      setAddValue('');
                      setIsAdding(false);
                    }}
                    clearActive={isAdding}
                  />
                </Box>
                <Box pad={{ vertical: '0.15rem' }}>
                  <AppButton
                    overrideHover
                    onClick={() => {
                      handleTableAction(addValue, null, 'push');
                      setAddValue('');
                      setIsAdding(false);
                    }}
                    disabled={addSubmitDisabled}
                    level="tableControls"
                    color={button.primaryText}
                    bgColor={button.primaryBg}
                    fontWeight={600}
                    label="Save"
                  />
                </Box>
              </Box>
            </Box>
          )}
          {renderTableContents()}
        </Box>
      </TableWrapper>
    </Box>
  );
};

DomainListTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  domainData: PropTypes.arrayOf(PropTypes.string),
  pageTitle: PropTypes.string,
  actionTerm: PropTypes.string.isRequired,
  headerMap: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  updateDomain: PropTypes.func,
  noTablePad: PropTypes.bool,
  unitsApiMode: PropTypes.bool,
  updateUnitsRequest: PropTypes.func,
  paymentPending: PropTypes.bool,
};

export default DomainListTable;
